
import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Clock, AlertTriangle, CheckCircle2, Zap, ArrowRight, X } from 'lucide-react';

const TimeEffortComparisonSection = () => {
  const { isMobile } = useBreakpoint();

  const withoutPackageTasks = [
    { task: "Research and write landing page copy", hours: "8-12 hours" },
    { task: "Design and build landing page", hours: "12-18 hours" },
    { task: "Create video sales letter script", hours: "6-8 hours" },
    { task: "Record and edit video", hours: "8-10 hours" },
    { task: "Design 6 business card templates", hours: "10-15 hours" },
    { task: "Write niche-specific copy", hours: "4-6 hours" },
  ];

  const withPackageSteps = [
    { step: "Download the bundle", time: "2 minutes" },
    { step: "Upload landing page to your hosting", time: "15 minutes" },
    { step: "Add your branding to video", time: "20 minutes" },
    { step: "Customize templates with your info", time: "8 minutes" },
  ];

  return (
    <SectionLayout 
      backgroundColor="bg-white" 
      paddingY="py-16 md:py-20 lg:py-24"
      containerWidth="wide"
    >
      <div className="text-center space-y-12 md:space-y-16">
        
        {/* Section Header */}
        <div className="space-y-6 animate-fade-in">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#003a70] to-[#003a70]/90 rounded-full shadow-lg">
            <Clock className="text-white" size={isMobile ? 24 : 28} />
            <span className="text-white font-bold text-lg md:text-xl">Time Comparison</span>
          </div>
          
          <h2 className={cn(
            "font-bold text-[#003a70] leading-tight max-w-4xl mx-auto",
            isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl xl:text-5xl"
          )}>
            Here's Why This 
            <span className="text-[#FEE6D8] bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">
              {" "}Changes Everything
            </span>
          </h2>
          
          <p className={cn(
            "text-[#003a70]/80 leading-relaxed max-w-3xl mx-auto",
            isMobile ? "text-lg" : "text-xl lg:text-2xl"
          )}>
            The difference between doing it yourself vs. getting our complete system
          </p>
        </div>

        {/* Comparison Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-7xl mx-auto">
          
          {/* WITHOUT Package */}
          <div className="relative bg-gradient-to-br from-red-50 to-red-100/50 rounded-3xl p-8 md:p-10 border-2 border-red-200/50 shadow-xl animate-fade-in" style={{ animationDelay: '0.1s' }}>
            {/* Warning Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-red-500 text-white px-6 py-2 rounded-full shadow-lg flex items-center gap-2">
                <AlertTriangle size={20} />
                <span className="font-bold text-sm">WITHOUT PACKAGE</span>
              </div>
            </div>
            
            <div className="mt-8 space-y-8">
              {/* Time Header */}
              <div className="text-center">
                <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Clock className="text-white" size={32} />
                </div>
                <h3 className={cn(
                  "font-bold text-red-700 mb-2",
                  isMobile ? "text-2xl" : "text-3xl"
                )}>
                  45-65 Hours
                </h3>
                <p className="text-red-600 font-medium">
                  7-10 weeks of work
                </p>
              </div>
              
              {/* Task List */}
              <div className="space-y-4">
                <h4 className="font-bold text-red-700 text-lg mb-4">What you'll need to do:</h4>
                {withoutPackageTasks.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-4 p-4 bg-white/60 rounded-xl border border-red-200/50"
                  >
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <X className="text-white" size={14} />
                    </div>
                    <div className="flex-1">
                      <p className="text-red-700 font-medium text-sm md:text-base">
                        {item.task}
                      </p>
                      <p className="text-red-600 text-sm mt-1 font-semibold">
                        {item.hours}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Bottom Summary */}
              <div className="bg-red-500/10 rounded-xl p-6 border-2 border-red-200">
                <p className="text-red-700 font-bold text-center">
                  Plus: No guarantee it will convert or look professional
                </p>
              </div>
            </div>
          </div>

          {/* WITH Package */}
          <div className="relative bg-gradient-to-br from-green-50 to-[#FEE6D8]/30 rounded-3xl p-8 md:p-10 border-2 border-[#FEE6D8]/50 shadow-xl animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {/* Success Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-gradient-to-r from-[#003a70] to-[#003a70]/90 text-white px-6 py-2 rounded-full shadow-lg flex items-center gap-2">
                <CheckCircle2 size={20} />
                <span className="font-bold text-sm">WITH PACKAGE</span>
              </div>
            </div>
            
            <div className="mt-8 space-y-8">
              {/* Time Header */}
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Zap className="text-white" size={32} />
                </div>
                <h3 className={cn(
                  "font-bold text-[#003a70] mb-2",
                  isMobile ? "text-2xl" : "text-3xl"
                )}>
                  45 Minutes
                </h3>
                <p className="text-[#003a70]/80 font-medium">
                  Same day setup
                </p>
              </div>
              
              {/* Steps List */}
              <div className="space-y-4">
                <h4 className="font-bold text-[#003a70] text-lg mb-4">Simple 4-step process:</h4>
                {withPackageSteps.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-4 p-4 bg-gradient-to-r from-white to-[#FEE6D8]/20 rounded-xl border border-[#FEE6D8]/30"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-[#003a70] font-medium text-sm md:text-base">
                        {item.step}
                      </p>
                      <p className="text-[#003a70]/70 text-sm mt-1 font-semibold">
                        {item.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Bottom Summary */}
              <div className="bg-gradient-to-r from-[#FEE6D8]/30 to-[#FEE6D8]/20 rounded-xl p-6 border-2 border-[#FEE6D8]/40">
                <p className="text-[#003a70] font-bold text-center">
                  Professionally designed, tested, and ready to convert
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Impact Statement */}
        <div className="max-w-5xl mx-auto animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="bg-gradient-to-r from-[#003a70] to-[#003a70]/90 rounded-3xl p-8 md:p-12 text-center shadow-2xl relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FEE6D8]/5 via-transparent to-[#FEE6D8]/10 pointer-events-none"></div>
            
            <div className="relative z-10 space-y-6">
              <div className="flex items-center justify-center gap-4 mb-6">
                <ArrowRight className="text-[#FEE6D8]" size={isMobile ? 32 : 40} />
              </div>
              
              <h3 className={cn(
                "font-bold text-white leading-tight",
                isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl xl:text-4xl"
              )}>
                That's a <span className="text-[#FEE6D8]">98.8% Time Savings</span>
              </h3>
              
              <p className={cn(
                "text-white/90 leading-relaxed max-w-3xl mx-auto",
                isMobile ? "text-base md:text-lg" : "text-lg lg:text-xl"
              )}>
                While others spend 2+ months building their system, 
                you'll be making sales by dinner tonight.
              </p>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 inline-block">
                <p className="text-[#FEE6D8] font-bold text-lg md:text-xl">
                  Time is money. This package saves you both.
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </SectionLayout>
  );
};

export default TimeEffortComparisonSection;
